import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { apiService } from '../services/api';
import './ProfilePhotoUpload.css';

function ProfilePhotoUpload({ patientId, patientName, onPhotoUpdate }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing photo
  React.useEffect(() => {
    loadPhoto();
  }, [patientId]);

  const loadPhoto = () => {
    // Try to load the photo, if it fails, set hasPhoto to false
    const photoUrl = apiService.getProfilePhotoUrl(patientId);
    const img = new Image();
    img.onload = () => {
      setPhotoUrl(photoUrl);
      setHasPhoto(true);
    };
    img.onerror = () => {
      setPhotoUrl(null);
      setHasPhoto(false);
    };
    img.src = photoUrl;
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File is too large. Maximum size is 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setShowModal(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleUploadCroppedImage = async () => {
    try {
      setUploading(true);
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const file = new File([croppedImageBlob], 'profile-photo.jpg', { type: 'image/jpeg' });
      
      await apiService.uploadProfilePhoto(patientId, file);
      
      setShowModal(false);
      setImageSrc(null);
      loadPhoto();
      
      if (onPhotoUpdate) {
        onPhotoUpdate();
      }
      
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await apiService.deleteProfilePhoto(patientId);
      setPhotoUrl(null);
      setHasPhoto(false);
      
      if (onPhotoUpdate) {
        onPhotoUpdate();
      }
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="profile-photo-container">
        <div className="profile-photo-circle">
          {hasPhoto ? (
            <img src={photoUrl} alt={patientName} className="profile-photo-img" />
          ) : (
            <div className="profile-photo-placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
          )}
          <div className="profile-photo-overlay">
            <button 
              className="photo-action-btn upload-btn"
              onClick={() => fileInputRef.current?.click()}
              title={hasPhoto ? "Change Photo" : "Upload Photo"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4m14-7l-5-5-5 5m5-5v12"/>
              </svg>
            </button>
            {hasPhoto && (
              <button 
                className="photo-action-btn delete-btn"
                onClick={handleDeletePhoto}
                title="Delete Photo"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {showModal && (
        <div className="photo-crop-modal-overlay">
          <div className="photo-crop-modal">
            <div className="modal-header">
              <h3>Position Your Photo</h3>
              <button className="close-btn" onClick={handleCancel}>×</button>
            </div>
            
            <div className="crop-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            
            <div className="crop-controls">
              <label className="zoom-label">
                <span>Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="zoom-slider"
                />
              </label>
            </div>
            
            <div className="modal-actions">
              <button 
                className="modal-btn cancel-modal-btn" 
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                className="modal-btn save-modal-btn" 
                onClick={handleUploadCroppedImage}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Save Photo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProfilePhotoUpload;

